import React from 'react';
import { View, TextInput, Pressable, ScrollView, Text } from 'react-native';
import { Search, X } from 'lucide-react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';

interface FilterChip {
  label: string;
  value: string;
  isActive: boolean;
}

interface FilterHeaderProps {
  /** Current search query */
  searchValue: string;
  /** Handler for search text changes */
  onSearchChange: (text: string) => void;
  /** Placeholder for the search input */
  placeholder?: string;
  /** Optional filter chips */
  filters?: FilterChip[];
  /** Handler when a filter chip is toggled */
  onFilterPress?: (value: string) => void;
  /** Optional right-side or below content */
  children?: React.ReactNode;
  /** Additional className for the container */
  className?: string;
}

/**
 * Search input with optional filter chips below.
 * Extracted from the search + filter chip pattern used in dealflow
 * and portfolio screens.
 *
 * Usage:
 * ```tsx
 * <FilterHeader
 *   searchValue={query}
 *   onSearchChange={setQuery}
 *   placeholder="Search startups..."
 *   filters={[
 *     { label: 'All', value: 'all', isActive: true },
 *     { label: 'Active', value: 'active', isActive: false },
 *   ]}
 *   onFilterPress={handleFilter}
 * />
 * ```
 */
export function FilterHeader({
  searchValue,
  onSearchChange,
  placeholder = 'Search...',
  filters,
  onFilterPress,
  children,
  className,
}: FilterHeaderProps) {
  const colors = useThemeColors();

  return (
    <View className={cn('gap-3', className)}>
      {/* Search Input */}
      <View
        className="flex-row items-center rounded-xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <View className="pl-4 pr-2 py-3">
          <Search size={18} color={colors.text.tertiary} />
        </View>
        <TextInput
          value={searchValue}
          onChangeText={onSearchChange}
          placeholder={placeholder}
          placeholderTextColor={colors.text.tertiary}
          className="flex-1 py-3 pr-2 text-base"
          style={{ color: colors.text.primary }}
          accessibilityLabel={placeholder}
          accessibilityRole="search"
        />
        {searchValue.length > 0 && (
          <Pressable
            onPress={() => onSearchChange('')}
            className="pr-4 py-3"
            accessibilityRole="button"
            accessibilityLabel="Clear search"
          >
            <X size={18} color={colors.text.tertiary} />
          </Pressable>
        )}
      </View>

      {/* Filter Chips */}
      {filters && filters.length > 0 && onFilterPress && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flexGrow: 0 }}
        >
          <View className="flex-row gap-2">
            {filters.map((chip) => (
              <Pressable
                key={chip.value}
                onPress={() => onFilterPress(chip.value)}
                className="px-4 py-2 rounded-full"
                style={{
                  backgroundColor: chip.isActive
                    ? colors.primary[600]
                    : colors.surface.primary,
                  borderWidth: 1,
                  borderColor: chip.isActive
                    ? colors.primary[600]
                    : colors.border.light,
                }}
                accessibilityRole="button"
                accessibilityLabel={`Filter: ${chip.label}`}
                accessibilityState={{ selected: chip.isActive }}
              >
                <Text
                  style={{
                    color: chip.isActive ? 'white' : colors.text.secondary,
                  }}
                  className="text-sm font-medium"
                >
                  {chip.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </ScrollView>
      )}

      {children}
    </View>
  );
}
